import {
  Box,
  Button,
  ChakraProvider,
  extendTheme,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Link,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { DISPLAY_MAGNITUDE_OPTIONS } from '@lib/enums/display-magnitude';
import { LEAD_TIME_FORMAT_OPTIONS } from '@lib/enums/lead-time-format';
import { formatWonLossStatus } from '@lib/enums/quote-won-loss-status';
import { SnapQuoteWonLossStatus } from '@snap-types/models/enums';
import {
  type SnapSiteConfigEditFormData,
  type SnapSiteConfigEditFormDataUnvalidated,
  snapSiteConfigEditSchema,
} from '@snap-types/models/snap-site-config';
import { FormDebug } from '@ui/components/FormDebug';
import { PageHeader } from '@ui/components/layout/PageHeader';
import { PageWrapper } from '@ui/components/layout/PageWrapper';
import { QueryError, QueryLoading } from '@ui/components/query';
import { Show } from '@ui/components/Show';
import { Router } from '@ui/config/routes';
import theme from '@ui/config/theme';
import { useUpdateSnapSiteConfigMutation } from '@ui/endpoints/update-snap-site-config';
import { useViewSnapSiteConfigQuery } from '@ui/endpoints/view-snap-site-config';
import { SnapSiteConfigEditContext } from '@ui/features/snap-site-config/screens/edit/context';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useValidationToast } from '@ui/hooks/useToast';
import { useState } from 'react';
import { FormProvider, get, useForm, useFormContext } from 'react-hook-form';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { FeatureVisibleSelect } from './components/FeatureVisibleSelect';
import { PriceStrategyConfigsForm } from './components/PriceStrategyConfigsForm';
import { PartInsightsPanel } from './PartInsightsPanel';

const customTheme = extendTheme(theme, {
  components: {
    FormLabel: {
      baseStyle: {
        fontSize: 'sm',
      },
    },
  },
});

export function SnapSiteConfigEdit() {
  const currentSite = useCurrentSite();
  const { snapSiteConfigUuid } = useParams() as { snapSiteConfigUuid: string };
  const viewSnapSiteConfigQuery = useViewSnapSiteConfigQuery({
    siteCode: currentSite.code,
    snapSiteConfigUuid,
  });

  if (viewSnapSiteConfigQuery.isLoading) {
    return <QueryLoading title="Loading Snap Site Config..." />;
  }

  if (viewSnapSiteConfigQuery.isError) {
    return <QueryError error={viewSnapSiteConfigQuery.error} />;
  }

  if (viewSnapSiteConfigQuery.isSuccess) {
    return (
      <SnapSiteConfigEditUI
        defaultValues={viewSnapSiteConfigQuery.data.snapSiteConfig}
        marketCodes={viewSnapSiteConfigQuery.data.marketCodes}
        discountCodes={viewSnapSiteConfigQuery.data.discountCodes}
      />
    );
  }

  return null;
}

function SnapSiteConfigEditUI({
  defaultValues,
  marketCodes,
  discountCodes,
}: {
  defaultValues?: Partial<SnapSiteConfigEditFormData>;
  marketCodes: string[];
  discountCodes: string[];
}) {
  const navigate = useNavigate();
  const currentSite = useCurrentSite();
  const { genericErrorToast, genericSuccessToast } = useValidationToast();
  const { snapSiteConfigUuid } = useParams() as { snapSiteConfigUuid: string };

  const [currentTab, setCurrentTab] = useState(0);

  const form = useForm({
    resolver: zodResolver(snapSiteConfigEditSchema),
    defaultValues: {
      decimalPlaces: 2,
      daysUntilExpectedWin: 2,
      daysUntilExpiration: 30,
      defaultWonLossStatus: SnapQuoteWonLossStatus.QUOTED,
      priceStrategies: [],
      ...defaultValues,
    },
    shouldUseNativeValidation: false,
  });

  const [updateSnapSiteConfig, updateSnapSiteConfigResult] =
    useUpdateSnapSiteConfigMutation();

  async function handleSubmit(data: SnapSiteConfigEditFormData) {
    try {
      await updateSnapSiteConfig({
        params: {
          siteCode: currentSite.code,
          snapSiteConfigUuid,
        },
        body: {
          snapSiteConfig: data,
        },
      }).unwrap();

      genericSuccessToast(
        'Site config updated successfully.',
        'Your changes have been saved.',
      );
    } catch (error) {
      console.error('Failed to update site config:', error);
      genericErrorToast('Failed to update site config.', 'Please try again.');
    }
  }

  return (
    <SnapSiteConfigEditContext.Provider value={{ marketCodes, discountCodes }}>
      <FormProvider {...form}>
        <PageWrapper>
          <PageHeader
            title={`Edit ${currentSite.code} Site Config`}
            subtitle={
              <Link
                as={RouterLink}
                to={Router.sitePath(currentSite, Router.routes.adminSites)}
              >
                ← Back to Sites
              </Link>
            }
          />

          <FormDebug />
          <PageLayout>
            <ChakraProvider theme={customTheme}>
              <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                <VStack
                  as="fieldset"
                  gap={8}
                  disabled={form.formState.isSubmitting}
                >
                  <Tabs
                    width="100%"
                    isLazy
                    lazyBehavior="keepMounted"
                    // display={isInfoVisible ? 'block' : 'none'}
                    index={currentTab}
                  >
                    <TabList>
                      <Tab onClick={() => setCurrentTab(0)}>
                        Basic Configuration
                      </Tab>
                      <Tab onClick={() => setCurrentTab(1)}>
                        Price Strategies
                      </Tab>
                      <Tab onClick={() => setCurrentTab(2)}>Part Insights</Tab>
                    </TabList>

                    <TabPanels>
                      <TabPanel key="normal">
                        <SnapSiteConfigForm />
                      </TabPanel>

                      <TabPanel key="price-strategy">
                        <PriceStrategyConfigsForm />
                      </TabPanel>

                      <TabPanel key="part-insights">
                        <PartInsightsPanel form={form} />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>

                  <Flex w="full" as="footer" justifyContent="flex-end" gap={4}>
                    <Button
                      type="button"
                      bg="snap.yellow.600"
                      rounded="sm"
                      _hover={{
                        backgroundColor: 'snap.yellow.700',
                      }}
                      onClick={() => {
                        // @TODO(shawk): confirm before navigate (losing form state)
                        form.reset();
                        navigate(
                          Router.sitePath(
                            currentSite,
                            Router.routes.adminSites,
                          ),
                        );
                      }}
                      isDisabled={updateSnapSiteConfigResult.isLoading}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      colorScheme="teal"
                      rounded="sm"
                      isLoading={updateSnapSiteConfigResult.isLoading}
                      loadingText="Saving..."
                    >
                      Save Changes
                    </Button>
                  </Flex>
                </VStack>
              </form>
            </ChakraProvider>
          </PageLayout>
        </PageWrapper>
      </FormProvider>
    </SnapSiteConfigEditContext.Provider>
  );
}

function PageLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box as="main" px={8} my={4} mt={6}>
      {children}
    </Box>
  );
}

function SnapSiteConfigForm() {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();
  const currentSite = useCurrentSite();

  return (
    <Flex direction="column" gap={4} w="full">
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          md: '1fr 1fr',
          lg: '1fr 1fr 1fr 1fr',
        }}
        gap={4}
      >
        <FormControl isInvalid={!!form.formState.errors.decimalPlaces}>
          <FormLabel htmlFor="decimal_places_input">Decimal Places:</FormLabel>

          {/* @NOTE(shawk): Ideally, this should be limited by the precision
          we set at the database column data type level. */}
          <Input
            id="decimal_places_input"
            type="number"
            inputMode="numeric"
            {...form.register('decimalPlaces')}
            min={0}
            max={10}
          />

          <ErrorMessage
            name="decimalPlaces"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />

          <FormHelperText>
            We recommend setting this no lower than 2.
          </FormHelperText>
        </FormControl>

        <FormControl isInvalid={!!form.formState.errors.daysUntilExpiration}>
          <FormLabel htmlFor="days_until_quote_expiration_input">
            Days Until Quote Expiration (default):
          </FormLabel>

          <Input
            id="days_until_quote_expiration_input"
            type="number"
            inputMode="numeric"
            {...form.register('daysUntilExpiration')}
            min={0}
          />

          <ErrorMessage
            name="daysUntilExpiration"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        <FormControl isInvalid={!!form.formState.errors.daysUntilExpectedWin}>
          <FormLabel htmlFor="days_until_expected_win_input">
            Days Until Expected Win (default):
          </FormLabel>

          <Input
            id="days_until_expected_win_input"
            type="number"
            inputMode="numeric"
            {...form.register('daysUntilExpectedWin')}
            min={0}
          />

          <ErrorMessage
            name="daysUntilExpectedWin"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        {/*
          @see {@link https://github.com/mwcomponents/quickquote/issues/2718}

          <FormControl isInvalid={!!form.formState.errors.partSpecTable}>
            <FormLabel htmlFor="part_spec_table_input">
              Part Spec Table:
            </FormLabel>

            <Select
              {...form.register('partSpecTable')}
              id="part_spec_table_input"
            >
              {Object.entries(PartSpecTable).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </Select>

            <ErrorMessage
              name="partSpecTable"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />

            <Text fontSize="sm" color="gray.500" mt={1}>
              Which table in Visual we use to look up Part Specs.
            </Text>
          </FormControl>
        */}

        <FormControl isInvalid={!!form.formState.errors.defaultWonLossStatus}>
          <FormLabel htmlFor="default_won_loss_status_input">
            Default Won/Loss Status:
          </FormLabel>

          <Select
            {...form.register('defaultWonLossStatus')}
            id="default_won_loss_status_input"
          >
            {Object.entries(SnapQuoteWonLossStatus).map(([key, value]) => (
              <option key={key} value={key}>
                {formatWonLossStatus(value)}
              </option>
            ))}
          </Select>

          <ErrorMessage
            name="defaultWonLossStatus"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        <FormControl
          isInvalid={!!get(form.formState.errors, 'quantityDisplayMagnitude')}
        >
          <FormLabel htmlFor="quantity_display_magnitude_input">
            Quantity Display Magnitude:
          </FormLabel>

          <Select
            {...form.register('quantityDisplayMagnitude')}
            id="quantity_display_magnitude_input"
          >
            {DISPLAY_MAGNITUDE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <ErrorMessage
            name="quantityDisplayMagnitude"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        <FormControl
          isInvalid={!!get(form.formState.errors, 'quantityDisplayMagnitude')}
        >
          <FormLabel htmlFor="lead_time_format_input">
            Show Lead Time Fields on PDF:
          </FormLabel>

          <Select
            {...form.register('leadTimeFormat')}
            id="lead_time_format_input"
          >
            {LEAD_TIME_FORMAT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <ErrorMessage
            name="leadTimeFormat"
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        {/* @NOTE(shawk): For now, we're keeping this simple. If we need
            more flexibility/expressiveness we can reconsider how we structure
            the configuration and rendering logic. */}
        <Show when={showPartRequirementFixedCost(currentSite)}>
          <FormControl
            isInvalid={
              !!get(form.formState.errors, 'defaultPartRequirementFixedCost')
            }
          >
            <FormLabel htmlFor="default_part_requirement_fixed_cost_input">
              Part Requirement Fixed Cost (default):
            </FormLabel>

            <Input
              id="default_part_requirement_fixed_cost_input"
              type="number"
              inputMode="numeric"
              {...form.register('defaultPartRequirementFixedCost')}
              min={0}
            />

            <ErrorMessage
              name="defaultPartRequirementFixedCost"
              render={({ message }) => (
                <FormErrorMessage>{message}</FormErrorMessage>
              )}
            />
          </FormControl>
        </Show>
        <FormControl>
          <FormLabel htmlFor="duplicate_quote_visible_input">
            Duplicate Quote Visibility
          </FormLabel>

          <FeatureVisibleSelect
            {...form.register('duplicateQuoteVisible')}
            id="duplicate_quote_visible_input"
          />
          {/* <option value={FeatureVisibility.NONE}>No One</option>
            <option value={FeatureVisibility.SUPER_ADMIN}>
              Super Admins Only
            </option>
            <option value={FeatureVisibility.ALL}>All Users</option>
          </Select> */}
        </FormControl>
      </Grid>
    </Flex>
  );
}

function showPartRequirementFixedCost(currentSite: { code: string }) {
  // Only show for CAP for now
  return ['CAP'].includes(currentSite.code.toUpperCase());
}
